<template>
  <v-dialog
    v-model="internalValue"
    max-width="860"
    scrollable
    transition="dialog-bottom-transition"
    content-class="premium-dialog"
  >
    <v-card class="premium-modal-card">
      <div class="premium-modal-header">
        <div class="premium-modal-header__icon">
          <v-icon color="#FFD54F" size="28">mdi-crown</v-icon>
        </div>
        <div>
          <h3 class="premium-modal-title">Associado Premium</h3>
          <p class="premium-modal-subtitle">
            Assinatura anual de R$ 297. Receba comissões quando associados da sua rede aderirem ao plano.
          </p>
        </div>
        <v-btn icon dark class="ml-auto" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="premium-modal-body">
        <v-alert
          v-if="potentialEarnings > 0"
          type="warning"
          dense
          border="left"
          colored-border
          class="mb-4 premium-potential-alert"
        >
          <strong>Você está deixando {{ formatCurrency(potentialEarnings) }} na mesa.</strong>
          Esse é o potencial de comissões de adesão na sua rede agora.
        </v-alert>

        <v-alert
          v-if="!pagbankConnected"
          type="info"
          dense
          outlined
          class="mb-4"
        >
          Você pode ser Premium sem conta PagBank, mas precisará conectá-la para
          receber comissões da rede.
        </v-alert>

        <div v-if="!hasQrcode" class="premium-modal-grid">
          <div class="premium-modal-grid__main">
            <div class="premium-step">
              <span class="premium-step__badge">1</span>
              <span>Revise os benefícios e informe o CPF do pagamento</span>
            </div>

            <div
              v-for="feature in features"
              :key="feature"
              class="premium-feature-item mb-2"
            >
              <v-icon color="#2dcc6b" size="18">mdi-check-circle</v-icon>
              <span>{{ feature }}</span>
            </div>

            <v-text-field
              v-model="internalDocument"
              label="CPF do responsável pelo pagamento"
              placeholder="000.000.000-00"
              :rules="documentRules"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-card-account-details"
              v-mask="'###.###.###-##'"
              class="mt-4"
            />

            <div class="premium-investment-highlight mt-2">
              <span class="investment-label">Assinatura única</span>
              <strong class="investment-value">R$ 297,00</strong>
              <span class="investment-footnote">Cashback de R$ 29,70 no app BRPoints · sem mensalidade</span>
            </div>

            <v-btn
              block
              large
              color="#e5183d"
              dark
              class="premium-payment-btn mt-4"
              :loading="loading"
              :disabled="loading"
              @click="handlePay"
            >
              <v-icon left>mdi-lightning-bolt</v-icon>
              Aderir ao Premium
            </v-btn>
          </div>

          <div class="premium-modal-grid__side">
            <h4 class="premium-side-title">Quanto você ganha por adesão</h4>
            <premium-commission-ladder locked />
            <p class="premium-side-foot">
              Associados não Premium continuam na sua árvore, mas só Premium recebe essas comissões.
            </p>
          </div>
        </div>

        <div v-else class="premium-payment-step text-center">
          <div class="premium-step premium-step--center mb-4">
            <span class="premium-step__badge">2</span>
            <span>Pague via PIX no app BRPoints</span>
          </div>
          <p class="premium-payment-guide-description">
            Abra <b>Pague e Ganhe</b> no app <b>BRPoints</b>, escaneie o QR ou copie o código.
          </p>
          <v-img :src="qrcodeImage" width="220" height="220" contain class="mx-auto premium-qr" />
          <v-btn class="mt-3" small color="#e5183d" dark @click="copyQRCode">
            <v-icon left small>mdi-content-copy</v-icon>
            Copiar código PIX
          </v-btn>
          <div v-if="showCopySuccess" class="premium-copy-feedback">Código copiado!</div>
          <v-btn
            class="mt-4"
            block
            outlined
            color="#2dcc6b"
            :loading="checkingPremium"
            @click="$emit('checkPremium')"
          >
            Já paguei — atualizar status
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import PremiumCommissionLadder from "@/components/premium/PremiumCommissionLadder.vue";
import { PREMIUM_BENEFITS, formatPremiumCurrency } from "@/config/premiumProgram";
import { computed, defineComponent, ref } from "vue";

export default defineComponent({
  name: "AssociatePremiumModal",
  components: { PremiumCommissionLadder },
  props: {
    value: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    checkingPremium: { type: Boolean, default: false },
    customerDocument: { type: String, default: "" },
    qrcodeImage: { type: String, default: "" },
    qrcodeText: { type: String, default: "" },
    pagbankConnected: { type: Boolean, default: false },
    potentialEarnings: { type: Number, default: 0 },
    features: {
      type: Array as () => string[],
      default: () => [...PREMIUM_BENEFITS],
    },
  },
  emits: ["input", "pay", "update:customerDocument", "checkPremium"],
  setup(props, { emit }) {
    const internalValue = computed({
      get: () => props.value,
      set: (value: boolean) => emit("input", value),
    });
    const internalDocument = computed({
      get: () => props.customerDocument,
      set: (value: string) => emit("update:customerDocument", value),
    });
    const hasQrcode = computed(() => Boolean(props.qrcodeImage));
    const showCopySuccess = ref(false);

    const documentRules = [
      (value: string) => {
        if (!value) return "CPF é obrigatório";
        const numeric = value.replace(/\D/g, "");
        return numeric.length === 11 || "Informe 11 dígitos";
      },
    ];

    const close = () => emit("input", false);
    const handlePay = () => emit("pay");

    const copyQRCode = async () => {
      if (!props.qrcodeText) return;
      await navigator.clipboard.writeText(props.qrcodeText);
      showCopySuccess.value = true;
      setTimeout(() => {
        showCopySuccess.value = false;
      }, 2000);
    };

    return {
      internalValue,
      internalDocument,
      hasQrcode,
      documentRules,
      close,
      handlePay,
      copyQRCode,
      showCopySuccess,
      formatCurrency: formatPremiumCurrency,
    };
  },
});
</script>

<style scoped lang="scss">
.premium-modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, #c41e3a, #e5183d);
  color: #fff;
  padding: 18px 20px;
}
.premium-modal-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.premium-modal-title {
  margin: 0;
  font-weight: 800;
  font-size: 1.2rem;
}
.premium-modal-subtitle {
  margin: 4px 0 0;
  font-size: 0.85rem;
  opacity: 0.92;
}
.premium-modal-body {
  padding: 20px 24px 24px;
}
.premium-modal-grid {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 24px;
}
.premium-modal-grid__side {
  background: #f8f9fc;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e8ecf4;
}
.premium-side-title {
  margin: 0 0 12px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #1a1a2e;
}
.premium-side-foot {
  margin: 12px 0 0;
  font-size: 0.72rem;
  color: #777;
  line-height: 1.4;
}
.premium-feature-item {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 0.88rem;
  line-height: 1.35;
}
.premium-step {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 0.88rem;
  margin-bottom: 14px;
  color: #1a1a2e;
}
.premium-step--center {
  justify-content: center;
}
.premium-step__badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e5183d;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.premium-investment-highlight {
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  background: linear-gradient(180deg, #fff5f7, #fafafa);
  border: 1px solid rgba(229, 24, 61, 0.15);
}
.investment-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #888;
}
.investment-value {
  display: block;
  font-size: 2rem;
  color: #e5183d;
  line-height: 1.1;
  margin: 4px 0;
}
.investment-footnote {
  font-size: 0.78rem;
  color: #666;
}
.premium-payment-guide-description {
  font-size: 0.9rem;
  margin-bottom: 12px;
}
.premium-qr {
  border: 8px solid #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}
.premium-copy-feedback {
  font-size: 0.8rem;
  margin-top: 8px;
  color: #2dcc6b;
}
.premium-potential-alert {
  background: #fff8e1 !important;
}
@media (max-width: 768px) {
  .premium-modal-grid {
    grid-template-columns: 1fr;
  }
}
</style>
